import * as PIXI from "pixi.js";
import { toDataRow, waitUntil, flyTrail, countUp } from "./chestCollection";
// NOT USED since the flying value takes its sizes from the coin and the collector:
// import { Settings } from "../../settings";
import { formatToCustomLocale } from "../../utilities";
import { createCoinLabel, setCoinLabel } from "./coinLabel";
import { playScarabReveal } from "./scarabReveal";

/**
 * endCollector — the end of Hold & Win (GDD "End of Hold & Win Feature
 * (Collector)"), up to the Total Win popup.
 *
 *   0  a breath: the SAME wait the loop takes before another respin
 *      (RESPIN_NEXT_DELAY_MS), so the player sees the finished board
 *   1  the respin counter goes out (counter_transform_N), IN FULL
 *   2  then the collector comes in, in the same spot (collector_in, in full)
 *      and settles into idle — collecting starts from there
 *   3  every valued cell in the active zone, in reading order — left to
 *      right, top to bottom — sends its fire trail into the collector,
 *      CARRYING its value (Pot Collection's value traveller: the number rides
 *      the trail's head); on arrival the collector plays `collector_hit`, its
 *      total counts up by that value, and the coin goes dark (`darken`)
 *   4  `collector_final_hit`, on the SERVER's paid amount (outcome.win),
 *      played IN FULL before the Total Win follows
 *
 * Each step waits for the previous animation's own complete callback — no
 * timers stacked on top of the spines (Vladimir, 2026-09-29: "let the player
 * have some time to breathe, to see the entire board").
 *
 * A press fires the remaining trails together — Pot Collection's slam stop.
 * The count never passes the paid amount (GDD: a capped round must not show
 * an uncapped total and count back).
 *
 * The Scarab reveal (GDD step 2) runs first — scarabReveal.js — and the
 * collection then collects the MULTIPLIED numbers it leaves on the board.
 * NOT YET: the Royal award (GDD step 6) — rare, never drawn on staging. The
 * final snap to the paid amount already includes it.
 *
 * Every delay runs on the ticker — never window timers.
 */

// The collector's number counting up to each arrival, ms (as the chests).
const COUNT_MS = 150;
// ── The counter -> collector handover ──
// The counter's out (counter_transform_N) is a 0.167 s shrink to nothing and
// the collector's in pops from nothing straight after — back to back it read
// as abrupt (Vladimir, 2026-09-29). The out plays at half speed (0.33 s) and
// the collector waits a beat once the counter is gone.
const COUNTER_OUT_TIME_SCALE = 0.5;
const COUNTER_TO_COLLECTOR_MS = 200;

/**
 * @param {object}   p
 * @param {Array}    p.values        the final grid, screen rows
 * @param {number}   p.unlockedRows  active rows (data rows 0..n-1)
 * @param {number}   p.win           what the round paid (subunits, capped)
 * @param {number}   p.settleDelayMs the wait before it all starts — the
 *                                   loop's own RESPIN_NEXT_DELAY_MS
 * @param {object}   p.overlay       HoldAndWinOverlay's handle
 * @param {object}   [p.trailLayer]  where the trails fly — above the whole
 *                                   room (the controller's trailLayer)
 * @param {object}   p.counter       HoldAndWinCounterController
 * @param {object}   p.collector     HoldAndWinCollectorController
 * @param {Function} p.isSkipped     true once the player pressed to skip
 * @returns {Promise<void>}
 */
export const playEndCollector = async ({
  values,
  unlockedRows,
  win,
  settleDelayMs = 0,
  overlay,
  trailLayer,
  counter,
  collector,
  isSkipped,
  // The settling step's scarabs (reveal order) and the server's product grid.
  scarabs = null,
  scarabMultipliers = null,
  // Clears the press flag between the reveal and the collection, so a press
  // that skipped the reveal does not skip the collection too.
  resetSkip = null,
}) => {
  // 0: the finished board, for as long as the next respin would have waited.
  await counter?.waitForIdle?.();
  if (settleDelayMs > 0) await waitUntil(() => false, settleDelayMs);
  // 0b: the Scarabs reveal and multiply their neighbours (GDD step 2) — on
  // the board, before the collector comes in. What they leave is collected.
  // eslint-disable-next-line no-param-reassign
  values = await playScarabReveal({
    scarabs,
    values,
    scarabMultipliers,
    unlockedRows,
    overlay,
    isSkipped,
  });
  resetSkip?.();
  // 1-2: the counter out IN FULL, then the collector in IN FULL (it settles
  // into idle by itself) — each on its own complete callback.
  await counter?.playOut?.(COUNTER_OUT_TIME_SCALE);
  await waitUntil(() => false, COUNTER_TO_COLLECTOR_MS);
  await collector?.show(0);

  // 3: the sources — every valued cell in the zone, left to right, top to
  // bottom (GDD "Collection Animation").
  const sources = [];
  if (values) {
    for (let r = 0; r < values[0].length; r++) {
      for (let c = 0; c < values.length; c++) {
        const v = values[c]?.[r];
        if (v > 0 && toDataRow(r) < unlockedRows) sources.push({ c, r, v });
      }
    }
  }

  const target = collector?.anchor;
  const layer = {
    trailContainer: trailLayer ?? overlay?.trailLayer?.(),
    symbolHeight: overlay?.cellHeight?.(),
  };
  let total = 0;

  if (target && layer.trailContainer && sources.length) {
    const targetGlobal = target.toGlobal(new PIXI.Point(0, 0));
    // ── Pot Collection's arrival, as the chests already do ── the count runs
    // but the next trail does not wait for it (executeCollect: _animateCounter
    // per arrival, _waitForCounters once at the end). Awaiting 150 ms per coin
    // made the collection drag (Vladimir, 2026-09-29: "bam bam and thats it").
    const counters = [];
    const arrive = (v) => {
      const before = total;
      total = Math.min(total + v, win > 0 ? win : total + v);
      collector.hit();
      counters.push(countUp(before, total, COUNT_MS, (n) => collector.setValue(n)));
    };
    // ── The flying number GROWS from the coin's number to the collector's ──
    //
    // EXACT at both ends, by construction:
    //   the texture  drawn at the collector total's own font size and style,
    //                so the label cache hands it the SAME texture the total
    //                uses — no rescaled pixels at the end
    //   the start    the coin's number as it stands on screen, read off the
    //                coin's own value sprite the moment it leaves
    //   the end      the total's scale on screen, read LIVE every frame (the
    //                collector's in/hit clips and a resize move it)
    // It grows with the head's progress, so it lands exactly the size of the
    // number it joins.
    const layerScale = () => {
      const wt = layer.trailContainer.worldTransform;
      return Math.hypot(wt.c, wt.d) || 1;
    };
    const worldScaleY = (obj) => {
      const wt = obj?.worldTransform;
      return wt ? Math.hypot(wt.c, wt.d) : 0;
    };
    const flyFrom = (src) => {
      const from = overlay.cellAnchor(src.c, src.r);
      const text = formatToCustomLocale(src.v);

      // The coin's number on screen, px (its sprite's texture height is its
      // font's; its world scale is everything scaling it).
      const coinLabel = overlay.valueLabel(src.c, src.r);
      const coinPx = (coinLabel?._fontSize || 0) * worldScaleY(coinLabel);

      const carried = createCoinLabel();
      setCoinLabel(carried, text, collector.fontSize, 0);
      const startScale = coinPx > 0 ? coinPx / (collector.fontSize * layerScale()) : 0;
      carried.scale.set(startScale);

      // The value MOVES: it leaves the coin the moment the trail fires and
      // rides the trail's head into the collector. The coin keeps nothing —
      // it plays `darken` and stays in its last frame.
      overlay.setValue(src.c, src.r, 0);
      overlay.darken(src.c, src.r);

      const grow = (p) => {
        if (carried.destroyed) return;
        const endScale = collector.textWorldScale() / layerScale();
        carried.scale.set(startScale + (endScale - startScale) * p);
      };
      return flyTrail(layer, from, targetGlobal, carried, grow).then(() => arrive(src.v));
    };

    for (let i = 0; i < sources.length; i++) {
      if (isSkipped()) {
        await Promise.all(sources.slice(i).map(flyFrom));
        break;
      }
      await flyFrom(sources[i]);
    }
    // The last count finishes before the collector lands on the paid amount.
    await Promise.all(counters);
  }

  // 4: the server's number, always — and the final hit played in full.
  collector?.setValue(win);
  await collector?.finalHit();
};
